/**
 * geogridScale.js — la escala de color del Scanner SEO.
 *
 * Fuente única para el mapa, la leyenda y el modo comparación. Si cada uno
 * eligiera su color, el usuario vería un punto verde en el mapa y ámbar en la
 * leyenda para la misma posición, y no habría forma de saber cuál miente.
 *
 * Dos colores por tramo, como en `healthScoreMetrics.js`:
 * - `color` rellena el disco del marcador. Es fondo, así que basta 3:1.
 * - `textColor` es el tono oscuro para números y etiquetas sobre blanco, que
 *   necesitan 4.5:1. Sin separarlos, un "4-7" en verde claro sobre fondo casi
 *   blanco se queda en 2:1 y no lo lee nadie.
 *
 * El número dentro del disco va SIEMPRE en blanco sobre el color de relleno;
 * todos los rellenos de esta escala pasan 4.5:1 contra blanco.
 */

// Umbrales por posición. El primer tramo es el "map pack" (top 3), que es lo
// único que ve un usuario de Google Maps sin desplegar más resultados: de ahí
// que sea su propio tramo y el más verde de todos.
const BUCKETS = [
  { key: 'top3', max: 3, color: '#059669', textColor: '#047857' },
  { key: 'top7', max: 7, color: '#65a30d', textColor: '#4d7c0f' },
  { key: 'top10', max: 10, color: '#d97706', textColor: '#b45309' },
  { key: 'top15', max: 15, color: '#ea580c', textColor: '#c2410c' },
  { key: 'top20', max: 20, color: '#ef4444', textColor: '#dc2626' },
  { key: 'beyond', max: Infinity, color: '#991b1b', textColor: '#991b1b' },
];

// Ausencia. Gris y NUNCA rojo: rojo es "vas fatal", gris es "no estás". Son
// cosas distintas y el usuario tiene que poder distinguirlas de un vistazo.
const ABSENT = { key: 'absent', color: '#94a3b8', textColor: '#475569' };

// Sin dato: el punto se consultó y DataForSEO no respondió a tiempo. Más claro
// que la ausencia y con "?": no es "no estás", es "no lo sabemos". Pintarlo como
// ausencia fue lo que hizo decir al mapa de la demo del 08/10 que el negocio no
// aparecía en su propia puerta. Espejo de `_FAILED` en `informe_mapa_png.py`.
const FAILED = { key: 'failed', color: '#cbd5e1', textColor: '#64748b' };
export const FAILED_LABEL = '?';

/** El tramo de una posición. `rank` a null/0 = el negocio no aparece. */
export function rankBucket(rank) {
  if (!rank || rank <= 0) return ABSENT;
  return BUCKETS.find((b) => rank <= b.max) || BUCKETS[BUCKETS.length - 1];
}

export const rankColor = (rank) => rankBucket(rank).color;
export const rankTextColor = (rank) => rankBucket(rank).textColor;

/**
 * Hasta dónde llega lo que el usuario ve como "estás en el mapa".
 *
 * Espejo de `TOP_N_VISIBLE` en `QR/gbusiness/services/onboarding.py`, que es
 * quien lo usa para la card del paso 4. NO es `GEOGRID_STORE_TOP_N` —que hoy
 * vale 20 también—: aquél dice cuántos competidores se guardan por punto, que
 * es otra cosa.
 */
export const TOP_N_VISIBLE = 20;

/**
 * ¿Este punto queda fuera de lo que el usuario ve como "apareces"?
 *
 * Sin posición (`null`/0) cuenta como fuera: no aparecer y aparecer el 47 se
 * leen igual desde el mapa, que en los dos casos escribe "·" o "20+".
 */
export const isOutsideTopN = (rank) => !rank || rank > TOP_N_VISIBLE;

/**
 * Lo que se escribe DENTRO del disco.
 *
 * Nunca un hueco ni un cero: un hueco se lee como error de carga y un cero como
 * "posición 0". Por encima de lo que pedimos se muestra "20+", que es lo que el
 * usuario entiende, aunque por dentro guardemos la posición real.
 */
export function rankLabel(rank, { absentLabel = '·' } = {}) {
  if (!rank || rank <= 0) return absentLabel;
  if (rank > TOP_N_VISIBLE) return `${TOP_N_VISIBLE}+`;
  return String(rank);
}

/**
 * Tramos para pintar la leyenda, en orden de mejor a peor. "Sin dato" solo
 * cuando el escaneo tiene algún punto fallido: en los demás sería ruido.
 */
export function legendBuckets({ withFailed = false } = {}) {
  return withFailed ? [...BUCKETS, ABSENT, FAILED] : [...BUCKETS, ABSENT];
}

// ── Modo comparación ──────────────────────────────────────────────────────
//
// Aquí la escala deja de ser secuencial y pasa a ser DIVERGENTE, porque el dato
// cambia de naturaleza: ya no es "qué tal voy" (0 → peor) sino "quién gana"
// (un cero es el punto neutro y hay señal a ambos lados). Usar la escala de
// posiciones para un delta pintaría de verde intenso los empates.

const DELTA_WIN = { color: '#059669', textColor: '#047857' };
const DELTA_LOSS = { color: '#dc2626', textColor: '#b91c1c' };
const DELTA_TIE = { color: '#64748b', textColor: '#475569' };
const DELTA_UNKNOWN = { color: '#cbd5e1', textColor: '#64748b' };

/**
 * `delta` = posición del competidor − la nuestra. Positivo = le ganamos.
 *
 * `null` es "no lo sabemos" (el competidor no está en el top guardado de ese
 * punto, o no estamos nosotros) y se pinta en gris claro, no en verde: dar por
 * ganado un punto sobre el que no hay dato es justo el error que convierte un
 * mapa en una mentira.
 */
export function deltaBucket(delta) {
  if (delta === null || delta === undefined) return DELTA_UNKNOWN;
  if (delta > 0) return DELTA_WIN;
  if (delta < 0) return DELTA_LOSS;
  return DELTA_TIE;
}

export const deltaColor = (delta) => deltaBucket(delta).color;

/** Etiqueta del disco en comparación: "+3" les ganamos por 3 posiciones. */
export function deltaLabel(delta, { unknownLabel = '·' } = {}) {
  if (delta === null || delta === undefined) return unknownLabel;
  if (delta > 0) return `+${delta}`;
  return String(delta);
}

// ── Modo grupo ────────────────────────────────────────────────────────────
//
// La escala vuelve a ser la de posiciones —el dato sigue siendo "qué tal voy",
// solo que el "yo" pasa a ser el grupo entero—, así que aquí no hay paleta
// nueva. Lo único que se añade es el ANILLO: los puntos donde el mejor del
// grupo no somos nosotros sino otro local del dueño llevan borde violeta en vez
// de blanco. Sin esa marca, el mapa de grupo se ve idéntico al de siempre pero
// con mejores números, que es exactamente la lectura equivocada.

export const SIBLING_RING = '#7c3aed';

// ── Tu negocio ────────────────────────────────────────────────────────────
//
// El halo oscuro que rodea el punto del propio negocio. Va FUERA del disco,
// separado por su borde blanco, para no tapar la posición: el dato sigue siendo
// el relleno y el número, el halo solo dice "esta es tu puerta". Sin él, en una
// rejilla de 25 discos iguales nadie sabía cuál era el suyo — y en la demo del
// 08/10, con ese punto sin dato, parecía que el negocio no aparecía en su propia
// calle. Slate oscuro: no choca con el violeta del grupo, el naranja del punto
// fijo del editor ni el gris de "sin dato".
export const BUSINESS_RING = '#1e293b';

// ── Tus otros locales ─────────────────────────────────────────────────────
//
// En una cuenta con varias fichas, las DEMÁS se pintan como una CHINCHETA con
// una tiendita: solo dicen "aquí tienes otro local", no se miden ni son de la
// rejilla. Es la forma la que lo separa de un punto escaneado, no el color —
// el verde también está en la escala (`top3`, `top7`) y un círculo verde
// dentro de la rejilla se leería como una buena posición. La chincheta
// además apunta con la punta, así que tapa poco el disco de al lado.
//
// Roja, como la chincheta de Google Maps: es lo que todo el mundo lee como
// "aquí hay un sitio". El rojo también existe en la escala (posiciones malas),
// pero la forma de gota con tiendita no se confunde con un disco numerado.
export const OWN_LOCATION_DOT = '#dc2626';

// Lado en píxeles del icono en el mapa. La punta está abajo en el centro.
export const OWN_LOCATION_PIN_SIZE = 30;

/**
 * La chincheta como SVG en un data URL. La usan el mapa (como `icon.url`) y la
 * leyenda (como `<img>`), para que las dos enseñen exactamente lo mismo. Se va
 * a imagen y no a `Symbol` porque un `Symbol` lleva un solo trazo de un solo
 * color, y la tiendita blanca dentro necesita dos.
 */
export function ownLocationPinUrl(color = OWN_LOCATION_DOT) {
  const svg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">'
    // Gota: círculo arriba, punta en (12, 23.5).
    + `<path d="M12 1C7.3 1 3.5 4.7 3.5 9.3c0 5.9 7.4 13.2 8.1 13.9a.6.6 0 0 0 .8 0c.7-.7 8.1-8 8.1-13.9C20.5 4.7 16.7 1 12 1z" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>`
    // Tiendita: toldo y fachada en blanco, la puerta en el color de la gota.
    + '<path d="M7.6 5.6h8.8l1.1 2.6H6.5z" fill="#ffffff"/>'
    + '<rect x="7.6" y="8.6" width="8.8" height="4.6" rx=".4" fill="#ffffff"/>'
    + `<rect x="10.9" y="10.2" width="2.2" height="3" fill="${color}"/>`
    + '</svg>';
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// Dos coordenadas a menos de ~1 m se tratan como el mismo sitio.
const SAME_SPOT_DEG = 1e-5;

const sameSpot = (a, b) => Math.abs(a.lat - b.lat) < SAME_SPOT_DEG
  && Math.abs(a.lng - b.lng) < SAME_SPOT_DEG;

/**
 * Los otros locales del usuario que se pueden pintar en el mapa.
 *
 * Fuera el local del mapa (por id) y cualquiera que caiga justo en `center`:
 * ahí ya está el halo del negocio y un punto encima lo taparía. Fuera también
 * los que no tienen coordenadas (áreas de servicio sin geocodificar), y uno
 * solo por sitio — el mismo local llega dos veces si está en dos conexiones.
 *
 * No se recorta por distancia: el encuadre no los tiene en cuenta, así que el
 * lejano simplemente aparece cuando el usuario mueve el mapa.
 *
 * @param {Array<{locationId?: string, id?: string, title?: string,
 *   address?: object, latitude?: number|null, longitude?: number|null}>} locations
 * @param {{excludeLocationId?: string|null,
 *   center?: {latitude?: number, longitude?: number}|null}} [options]
 * @returns {Array<{id: string, lat: number, lng: number, title: string, address: object|null}>}
 */
export function ownLocationMarkers(locations, { excludeLocationId = null, center = null } = {}) {
  const centerSpot = center?.latitude != null && center?.longitude != null
    ? { lat: center.latitude, lng: center.longitude }
    : null;
  const markers = [];
  for (const loc of locations || []) {
    const id = loc.locationId || loc.id;
    if (!id || id === excludeLocationId) continue;
    if (loc.latitude == null || loc.longitude == null) continue;
    const spot = { lat: Number(loc.latitude), lng: Number(loc.longitude) };
    if (!Number.isFinite(spot.lat) || !Number.isFinite(spot.lng)) continue;
    if (centerSpot && sameSpot(spot, centerSpot)) continue;
    if (markers.some((m) => m.id === id || sameSpot(m, spot))) continue;
    markers.push({ id, ...spot, title: loc.title || '', address: loc.address || null });
  }
  return markers;
}

// Tolerancia para decir que un punto ES el centro: los dos salen de los mismos
// números (el centro de la rejilla se construye sobre `scan.center`), así que
// basta con absorber el redondeo de coma flotante.
const SAME_SPOT = 1e-6;

/**
 * ¿Este punto es el del propio negocio?
 *
 * En «Puntos a medida» lo dice el servidor (`anchor`). En la rejilla es el nodo
 * que cae justo en `center`, que la máscara del mar nunca reubica. Un punto
 * reubicado no lo es nunca, aunque quedara cerca.
 */
export function isBusinessPoint(point, center) {
  if (!point) return false;
  if (point.anchor) return true;
  if (point.relocated || !center) return false;
  const lat = point.lat ?? point.latitude;
  const lng = point.lng ?? point.longitude;
  if (lat == null || lng == null) return false;
  return Math.abs(lat - center.latitude) < SAME_SPOT
    && Math.abs(lng - center.longitude) < SAME_SPOT;
}

/**
 * Props del marcador de un punto, ya resueltas.
 *
 * Se extrae del componente del mapa a propósito: así la lógica de color y
 * etiqueta se puede testear sin montar Google Maps ni mockear la librería.
 */
export function buildMarkerProps(
  point, { mode = 'mine', comparison = null, group = null } = {},
) {
  // Un punto sin dato lo es en TODOS los modos: ni nuestra posición, ni la del
  // competidor, ni la del grupo se midieron ahí.
  if (point?.failed) {
    return {
      color: FAILED.color, label: FAILED_LABEL, rank: null, delta: null, failed: true,
    };
  }

  if (mode === 'group') {
    const entry = (group?.points || []).find(
      (p) => p.i === point.i && p.j === point.j,
    );
    const rank = entry ? entry.rank : null;
    return {
      color: rankColor(rank),
      label: rankLabel(rank),
      rank,
      delta: null,
      // `placeId` con valor = ese punto lo cubre un HERMANO, no nosotros.
      fromSibling: !!entry?.placeId,
    };
  }

  if (mode === 'delta') {
    const entry = (comparison?.points || []).find(
      (p) => p.i === point.i && p.j === point.j,
    );
    const delta = entry ? entry.delta : null;
    const bucket = deltaBucket(delta);
    return { color: bucket.color, label: deltaLabel(delta), rank: null, delta };
  }

  if (mode === 'theirs') {
    const entry = (comparison?.points || []).find(
      (p) => p.i === point.i && p.j === point.j,
    );
    const rank = entry ? entry.theirs : null;
    return { color: rankColor(rank), label: rankLabel(rank), rank, delta: null };
  }

  return {
    color: rankColor(point.rank),
    label: rankLabel(point.rank),
    rank: point.rank,
    delta: null,
  };
}
